import React, { useState } from "react";

import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { HelpBox, SearchBox } from "@/components/atoms";
import { InsightFilters, InsightFiltersContext } from "@/context";
import {
  insightLevelOptions,
  insightScoreOptions,
  insightSortOptions,
} from "@/utils";
import { ScoreType, TabOption } from "@/types";

type InsightLayoutProps = {
  tab: TabOption;
  children: React.ReactNode;
};

const InsightLayout: React.FC<InsightLayoutProps> = ({ tab, children }) => {
  const [filters, setFilters] = useState<InsightFilters>({
    scoreType: { value: "score_wt", label: "Index" },
    levels: [1, 2, 3],
    searchText: "",
    sortType: "desc",
  });

  const anyFilters = tab.showScores || tab.showLevels || tab.showSearch;

  const handleScoreToggle = (
    event: React.MouseEvent<HTMLElement>,
    value: string | null,
  ) => {
    const newScoreType: ScoreType | undefined = insightScoreOptions.find(
      (option) => option.value === value,
    );
    value &&
      newScoreType &&
      setFilters((prev) => ({ ...prev, scoreType: newScoreType }));
  };

  const handleLevelToggle = (
    event: React.MouseEvent<HTMLElement>,
    values: number[],
  ) => {
    setFilters((prev) => ({ ...prev, levels: values }));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFilters((prev) => ({ ...prev, searchText: event.target.value || "" }));

  const toLevelLabel = () => {
    if (filters.levels.length === 1) {
      return insightLevelOptions.filter((x) => x.value === filters.levels[0])[0]
        .label;
    } else if (filters.levels.length === 2) {
      return insightLevelOptions
        .filter((x) => filters.levels.includes(x.value))
        .map((x) => x.short)
        .join(" + ");
    } else {
      return "All";
    }
  };

  const toSortLabel = () => {
    return insightSortOptions.filter((x) => x.value === filters.sortType)[0]
      .label;
  };

  const handleSortTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null,
  ) => {
    if (newValue) {
      setFilters((prev) => ({ ...prev, sortType: newValue }));
    }
  };

  return (
    <InsightFiltersContext.Provider value={{ filters, setFilters }}>
      {/* Data Options are pinned to the right hand side */}
      {anyFilters && (
        <Grid
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography variant="h6">{tab.label}</Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {tab.showSearch && (
              <Box display="flex" alignItems="flex-end">
                <SearchBox value={filters.searchText} onSearch={handleSearch} />
              </Box>
            )}
            {tab.showScores && (
              <Stack>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                >
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Score:
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    {filters.scoreType.label}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={filters.scoreType.value}
                  onChange={handleScoreToggle}
                >
                  {insightScoreOptions.map((option, index) => (
                    <HelpBox
                      key={`score-toggle-${index}`}
                      title={option.description}
                    >
                      <ToggleButton value={option.value}>
                        {option.icon}
                      </ToggleButton>
                    </HelpBox>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}
            {tab.showLevels && (
              <Stack>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                >
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Level:
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    {toLevelLabel()}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  value={filters.levels}
                  onChange={handleLevelToggle}
                >
                  {insightLevelOptions.map((option, index) => (
                    <HelpBox
                      key={`level-toggle-${index}`}
                      title={option.description}
                    >
                      <ToggleButton value={option.value}>
                        {option.icon}
                      </ToggleButton>
                    </HelpBox>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )}
            {tab.showSorts && (
              <Stack>
                <Box display="flex" flexDirection="column">
                  <Box display="flex" flexDirection="row">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      Sort:
                    </Typography>
                    <Typography variant="body2" color="secondary">
                      {toSortLabel()}
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={filters.sortType}
                    onChange={handleSortTypeChange}
                    exclusive
                    size="small"
                    aria-label="sort direction"
                  >
                    {insightSortOptions.map((option, index) => (
                      <HelpBox
                        key={`sort-toggle-${index}`}
                        title={option.description}
                      >
                        <ToggleButton value={option.value}>
                          {option.icon}
                        </ToggleButton>
                      </HelpBox>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              </Stack>
            )}
          </Stack>
        </Grid>
      )}

      {/* Content */}
      {children}
    </InsightFiltersContext.Provider>
  );
};

export default InsightLayout;
